import { useState, useEffect, useRef, useCallback } from 'react';

/**
 * Custom hook for infinite scroll pagination
 * @param {Function} fetchData - Function that fetches data (page, limit) => Promise
 * @param {Object} options - Configuration options
 * @returns {Object} - { data, loading, error, hasMore, loadMore, reset, observerTarget, isInitialLoad }
 */
export const useInfiniteScroll = (fetchData, options = {}) => {
  const {
    initialPage = 1,
    pageSize = 20,
    threshold = 200, // pixels from bottom to trigger load
    enabled = true,
    onError = null,
    maxItemsInMemory = null // Optional: limit items in memory (e.g., 1000 for large datasets)
  } = options;

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(initialPage);
  const [hasMore, setHasMore] = useState(true);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  
  const observerTarget = useRef(null);
  const isLoadingRef = useRef(false);

  const loadData = useCallback(async (pageNum, append = false) => {
    if (isLoadingRef.current) return;
    
    try {
      isLoadingRef.current = true;
      setLoading(true);
      setError(null);

      const response = await fetchData(pageNum, pageSize);
      
      // Handle both paginated and legacy response formats
      let items = [];
      let pagination = null;
      
      if (response.data) {
        if (response.data.pagination) {
          // Paginated response format
          pagination = response.data.pagination;
          // Extract items from response (could be campaigns, calls, scripts, etc.)
          items = response.data.campaigns || 
                  response.data.calls || 
                  response.data.contacts ||
                  response.data.scripts || 
                  response.data.recordings || 
                  response.data.sips || 
                  response.data.analytics || 
                  response.data.models ||
                  response.data;
        } else if (Array.isArray(response.data)) {
          // Legacy array format
          items = response.data;
        } else {
          // Try to find array in response.data
          items = Object.values(response.data).find(Array.isArray) || [];
        }
      } else if (Array.isArray(response)) {
        items = response;
      }

      if (append) {
        setData(prev => {
          const newData = [...prev, ...items];
          // Limit memory usage for large datasets
          if (maxItemsInMemory && newData.length > maxItemsInMemory) {
            // Keep only the most recent items (sliding window)
            return newData.slice(-maxItemsInMemory);
          }
          return newData;
        });
      } else {
        setData(items);
      }

      // Update hasMore based on pagination or items length
      if (pagination) {
        setHasMore(pagination.hasNextPage || false);
      } else {
        setHasMore(items.length === pageSize);
      }

      setIsInitialLoad(false);
    } catch (err) {
      console.error('Error loading data:', err);
      setError(err.message || 'Failed to load data');
      if (onError) {
        onError(err);
      }
    } finally {
      setLoading(false);
      isLoadingRef.current = false;
    }
  }, [fetchData, pageSize, onError]);

  // Initial load
  useEffect(() => {
    if (enabled && isInitialLoad) {
      loadData(initialPage, false);
    }
  }, [enabled, isInitialLoad, initialPage, loadData]);

  // Intersection Observer for infinite scroll
  useEffect(() => {
    if (!enabled || !hasMore || loading) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loading && !isLoadingRef.current) {
          const nextPage = page + 1;
          setPage(nextPage);
          loadData(nextPage, true);
        }
      },
      {
        rootMargin: `${threshold}px`,
        threshold: 0.1
      }
    );

    const currentTarget = observerTarget.current;
    if (currentTarget) {
      observer.observe(currentTarget);
    }

    return () => {
      if (currentTarget) {
        observer.unobserve(currentTarget);
      }
    };
  }, [enabled, hasMore, loading, page, threshold, loadData]);

  const loadMore = useCallback(() => {
    if (!loading && hasMore && !isLoadingRef.current) {
      const nextPage = page + 1;
      setPage(nextPage);
      loadData(nextPage, true);
    }
  }, [loading, hasMore, page, loadData]);

  const reset = useCallback(() => {
    setData([]);
    setPage(initialPage);
    setHasMore(true);
    setError(null);
    setIsInitialLoad(true);
    isLoadingRef.current = false;
  }, [initialPage]);

  return {
    data,
    loading,
    error,
    hasMore,
    loadMore,
    reset,
    observerTarget,
    isInitialLoad
  };
};

